/**
 * Approximate visible-light wavelength → sRGB hex string.
 * Based on Dan Bruton's algorithm. Wavelengths in nm (380–780).
 * Outside the visible range, returns a soft white/UV-ish glow.
 */
export function wavelengthToHex(wavelengthNm: number): string {
  let r = 0;
  let g = 0;
  let b = 0;
  const w = wavelengthNm;

  if (w >= 380 && w < 440) {
    r = -(w - 440) / (440 - 380);
    g = 0;
    b = 1;
  } else if (w >= 440 && w < 490) {
    r = 0;
    g = (w - 440) / (490 - 440);
    b = 1;
  } else if (w >= 490 && w < 510) {
    r = 0;
    g = 1;
    b = -(w - 510) / (510 - 490);
  } else if (w >= 510 && w < 580) {
    r = (w - 510) / (580 - 510);
    g = 1;
    b = 0;
  } else if (w >= 580 && w < 645) {
    r = 1;
    g = -(w - 645) / (645 - 580);
    b = 0;
  } else if (w >= 645 && w <= 780) {
    r = 1;
    g = 0;
    b = 0;
  } else {
    // UV / IR - render as faint violet / deep red so it's still visible
    if (w < 380) {
      r = 0.4;
      g = 0;
      b = 0.7;
    } else {
      r = 0.5;
      g = 0;
      b = 0;
    }
  }

  // Intensity falloff at the edges of the visible spectrum
  let intensity = 1;
  if (w >= 380 && w < 420) intensity = 0.3 + (0.7 * (w - 380)) / 40;
  else if (w >= 645 && w <= 780) intensity = 0.3 + (0.7 * (780 - w)) / 135;

  const to255 = (c: number) =>
    Math.max(0, Math.min(255, Math.round(255 * Math.pow(c * intensity, 0.8))));
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(to255(r))}${hex(to255(g))}${hex(to255(b))}`;
}

/**
 * Hydrogen-like Rydberg approximation: energy gap between shells n_low → n_high.
 * Returns an emitted photon wavelength in nm when an electron drops from
 * n_high back down to n_low. Used purely for visual color choice.
 */
export function transitionWavelengthNm(nLow: number, nHigh: number): number {
  // 1/λ = R · (1/n_low² − 1/n_high²)  with R ≈ 1.097e7 m⁻¹
  // λ in metres, then × 1e9 → nm
  const R = 1.097e7;
  const inv = R * (1 / (nLow * nLow) - 1 / (nHigh * nHigh));
  if (inv <= 0) return 700;
  const lambdaM = 1 / inv;
  return lambdaM * 1e9;
}
